/**
 * contentEditableUtils.ts — Shared pure DOM helpers for contentEditable
 * text editors with inline mention chips.
 *
 * Extracted from RichTextBox.tsx to share across ChatInput,
 * InlineMentionComposer, and RichTextBox itself.
 */

import React, { memo, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getThumbnailUrl } from '../../api/useAssetUrl'
import type { MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { NODE_TYPE_ICON, NODE_TYPE_ICON_SVG } from '../canvas-v2/nodes/shared/nodeTypeIcons'
import { hideMentionThumbnail, MENTION_CHIP_PRESENTATION } from './MentionChip'

// Re-export MentionCandidate for convenience
export type { MentionCandidate }

// ─── Pure DOM helpers ───────────────────────────────────────────────────────

/**
 * Create a mention chip `<span contentEditable="false">` element.
 * Includes optional thumbnail image, icon, label, and a remove button.
 */
export function createMentionChipElement(
  candidate: MentionCandidate,
  projectId?: string,
): HTMLSpanElement {
  const chip = document.createElement('span')
  chip.contentEditable = 'false'
  chip.dataset.mentionPill = 'true'
  chip.dataset.nodeId = candidate.id
  chip.dataset.label = candidate.label
  chip.dataset.icon = candidate.icon
  chip.dataset.mentionStatus = 'resolved'
  chip.className = MENTION_CHIP_PRESENTATION.rootClassName
  chip.setAttribute('data-testid', `mention-chip-${candidate.id}`)

  // Thumbnail (if available)
  if (candidate.thumbnailPath && projectId) {
    const thumb = document.createElement('img')
    thumb.src = getThumbnailUrl(projectId, candidate.thumbnailPath, 0) ?? ''
    thumb.alt = ''
    thumb.className = MENTION_CHIP_PRESENTATION.thumbnailClassName
    thumb.onerror = () => hideMentionThumbnail(thumb)
    chip.appendChild(thumb)
  }

  // Label (clickable)
  const label = document.createElement('span')
  label.dataset.mentionChipLabel = 'true'
  label.className = MENTION_CHIP_PRESENTATION.labelClassName
  label.setAttribute('data-testid', `mention-chip-label-${candidate.id}`)

  const icon = document.createElement('span')
  icon.className = MENTION_CHIP_PRESENTATION.iconClassName
  const svgMarkup = NODE_TYPE_ICON_SVG[candidate.type]
  if (svgMarkup) {
    icon.innerHTML = svgMarkup
    const svgEl = icon.querySelector('svg')
    if (svgEl) {
      svgEl.style.width = '100%'
      svgEl.style.height = '100%'
    }
  } else {
    icon.className = MENTION_CHIP_PRESENTATION.fallbackIconClassName
    icon.textContent = candidate.icon
  }

  const text = document.createElement('span')
  text.className = MENTION_CHIP_PRESENTATION.textClassName
  text.textContent = candidate.label

  label.append(icon, text)

  // Remove button
  const remove = document.createElement('button')
  remove.type = 'button'
  remove.contentEditable = 'false'
  remove.dataset.mentionChipRemove = 'true'
  remove.className = MENTION_CHIP_PRESENTATION.removeClassName
  remove.title = 'Remove reference'
  remove.setAttribute('aria-label', `Remove ${candidate.label} reference`)
  remove.setAttribute('data-testid', `mention-chip-remove-${candidate.id}`)
  remove.textContent = '\u00d7'

  chip.append(label, remove)
  return chip
}

function createMissingMentionChipElement(
  nodeId: string,
  projectId?: string,
): HTMLSpanElement {
  const chip = createMentionChipElement({
    id: nodeId,
    type: 'unknown',
    label: `${nodeId.slice(0, 8)}...`,
    icon: '',
  }, projectId)
  chip.dataset.mentionStatus = 'missing'
  chip.className = chip.className
    .replace('border-primary/25', 'border-white/10')
    .replace('bg-primary/15', 'bg-white/5')
    .replace('text-primary', 'text-foreground/40')
  chip.style.textDecoration = 'line-through'
  return chip
}

interface SelectionPoint {
  node: globalThis.Node
  offset: number
}

interface EditorSelectionSnapshot {
  anchor: SelectionPoint
  focus: SelectionPoint
  start: SelectionPoint
  end: SelectionPoint
}

interface ChipReplacement {
  previous: HTMLElement
  next: HTMLElement
}

function captureEditorSelection(editor: HTMLElement): EditorSelectionSnapshot | null {
  const selection = window.getSelection()
  if (
    !selection
    || selection.rangeCount === 0
    || !selection.anchorNode
    || !selection.focusNode
    || !editor.contains(selection.anchorNode)
    || !editor.contains(selection.focusNode)
  ) {
    return null
  }

  const range = selection.getRangeAt(0)
  return {
    anchor: { node: selection.anchorNode, offset: selection.anchorOffset },
    focus: { node: selection.focusNode, offset: selection.focusOffset },
    start: { node: range.startContainer, offset: range.startOffset },
    end: { node: range.endContainer, offset: range.endOffset },
  }
}

function descendantPath(ancestor: globalThis.Node, descendant: globalThis.Node): number[] | null {
  const path: number[] = []
  let current: globalThis.Node | null = descendant
  while (current && current !== ancestor) {
    const parent: ParentNode | null = current.parentNode
    if (!parent) return null
    let childIndex = -1
    for (let index = 0; index < parent.childNodes.length; index += 1) {
      if (parent.childNodes.item(index) === current) {
        childIndex = index
        break
      }
    }
    if (childIndex < 0) {
      throw new Error('Selection node is not present in its DOM parent')
    }
    path.unshift(childIndex)
    current = parent
  }
  return current === ancestor ? path : null
}

function resolveDescendantPath(root: globalThis.Node, path: number[]): globalThis.Node {
  let current = root
  for (const childIndex of path) {
    const child = current.childNodes.item(childIndex)
    if (!child) break
    current = child
  }
  return current
}

function clampSelectionOffset(node: globalThis.Node, offset: number): number {
  const maxOffset = node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE
    ? node.textContent?.length ?? 0
    : node.childNodes.length
  return Math.min(offset, maxOffset)
}

function mapSelectionPoint(
  point: SelectionPoint,
  replacements: ChipReplacement[],
): SelectionPoint {
  for (const replacement of replacements) {
    if (!replacement.previous.contains(point.node)) continue
    const path = descendantPath(replacement.previous, point.node)
    const node = path ? resolveDescendantPath(replacement.next, path) : replacement.next
    return { node, offset: clampSelectionOffset(node, point.offset) }
  }
  return { node: point.node, offset: clampSelectionOffset(point.node, point.offset) }
}

function restoreEditorSelection(
  snapshot: EditorSelectionSnapshot,
  replacements: ChipReplacement[],
) {
  const selection = window.getSelection()
  if (!selection) return

  const anchor = mapSelectionPoint(snapshot.anchor, replacements)
  const focus = mapSelectionPoint(snapshot.focus, replacements)
  if (typeof selection.setBaseAndExtent === 'function') {
    selection.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset)
    return
  }

  const start = mapSelectionPoint(snapshot.start, replacements)
  const end = mapSelectionPoint(snapshot.end, replacements)
  const range = document.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Refresh mention chips when the live canvas candidate set changes without
 * re-hydrating (and therefore overwriting) the editor's surrounding text or
 * moving an active selection across a replaced chip.
 */
export function reconcileMentionChips(
  editor: HTMLElement,
  candidates: MentionCandidate[],
  projectId?: string,
) {
  const candidatesById = new Map(candidates.map((candidate) => [candidate.id, candidate]))
  const replacements: ChipReplacement[] = []
  for (const chip of editor.querySelectorAll<HTMLElement>('[data-mention-pill="true"]')) {
    const nodeId = chip.dataset.nodeId
    if (!nodeId) continue
    const candidate = candidatesById.get(nodeId)
    const expectedStatus = candidate ? 'resolved' : 'missing'
    const expectedLabel = candidate?.label ?? `${nodeId.slice(0, 8)}...`
    if (chip.dataset.mentionStatus === expectedStatus && chip.dataset.label === expectedLabel) {
      continue
    }
    replacements.push({
      previous: chip,
      next: candidate
        ? createMentionChipElement(candidate, projectId)
        : createMissingMentionChipElement(nodeId, projectId),
    })
  }

  if (replacements.length === 0) return
  const selectionSnapshot = captureEditorSelection(editor)
  for (const replacement of replacements) {
    replacement.previous.replaceWith(replacement.next)
  }
  if (selectionSnapshot) {
    restoreEditorSelection(selectionSnapshot, replacements)
  }
}

/**
 * Recursively serialize a DOM node tree into plain text, converting mention
 * chips to `@[[nodeId]]` markers and Director skill chips to
 * `[[/skill-name]]` markers.
 */
interface SerializeNodeTextOptions {
  selectionFragment?: boolean
}

export function serializeNodeText(
  node: globalThis.Node,
  root: HTMLElement,
  options: SerializeNodeTextOptions = {},
): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? ''
  }
  if (!(node instanceof HTMLElement)) return ''

  const element = node
  if (element.dataset.mentionPill === 'true') {
    const nodeId = element.dataset.nodeId
    return nodeId ? `@[[${nodeId}]]` : ''
  }
  if (element.dataset.skillChip === 'true') {
    const skillName = element.dataset.skillName
    if (!skillName) {
      throw new Error('Director skill chip is missing data-skill-name')
    }
    return `[[/${skillName}]]`
  }
  if (element.tagName === 'BR') return '\n'

  const text = Array.from(node.childNodes).reduce((serialized, child) => {
    const childText = serializeNodeText(child, root, options)
    const childStartsBlock =
      child instanceof HTMLElement &&
      (child.tagName === 'DIV' || child.tagName === 'P')
    const childIsEmptyTrailingSelectionBlock =
      options.selectionFragment === true &&
      element === root &&
      child === node.lastChild &&
      childStartsBlock &&
      childText === '\n' &&
      !child.querySelector('br')
    const blockBoundary =
      childStartsBlock &&
      !childIsEmptyTrailingSelectionBlock &&
      serialized.length > 0 &&
      !serialized.endsWith('\n')
        ? '\n'
        : ''
    return `${serialized}${blockBoundary}${childText}`
  }, '')

  if (element !== root && (element.tagName === 'DIV' || element.tagName === 'P')) {
    return text.endsWith('\n') ? text : `${text}\n`
  }
  return text
}

/**
 * Normalize whitespace and NBSP in plain text.
 */
export function normalizePlainText(text: string): string {
  return text
    .replace(/\u00A0/g, ' ')
    .replace(/[^\S\r\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Get normalized plain text from a contentEditable editor element.
 */
export function getPlainTextFromEditor(editor: HTMLElement | null): string {
  if (!editor) return ''
  return normalizePlainText(serializeNodeText(editor, editor))
}

/**
 * Get deduplicated, ordered node IDs from all mention chips in the editor.
 */
export function getReferencedNodeIdsFromEditor(editor: HTMLElement | null): string[] {
  if (!editor) return []
  const seen = new Set<string>()
  const ordered: string[] = []
  editor.querySelectorAll<HTMLElement>('[data-mention-pill]').forEach((chip) => {
    const nodeId = chip.dataset.nodeId
    if (nodeId && !seen.has(nodeId)) {
      seen.add(nodeId)
      ordered.push(nodeId)
    }
  })
  return ordered
}

/**
 * Check whether the current selection anchor is inside the editor element.
 */
export function selectionInsideEditor(
  editor: HTMLElement,
  selection: Selection | null,
): boolean {
  if (!selection || selection.rangeCount === 0) return false
  return editor.contains(selection.anchorNode)
}

/**
 * Place the caret at the end of a contentEditable element and focus it.
 */
export function placeCaretAtEnd(editor: HTMLElement) {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(editor)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
  editor.focus()
}

/**
 * Hydrate raw text with `@[[nodeId]]` markers into mention chip elements.
 * Unknown node IDs get fallback chips with muted style.
 */
export function hydrateContent(
  editor: HTMLElement,
  content: string,
  candidates: MentionCandidate[],
  projectId?: string,
) {
  const MARKER_RE = /@\[\[([^\]]+)\]\]/g
  const fragment = document.createDocumentFragment()
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MARKER_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      fragment.appendChild(
        document.createTextNode(content.slice(lastIndex, match.index)),
      )
    }
    const nodeId = match[1]
    const candidate = candidates.find((c) => c.id === nodeId)
    if (candidate) {
      fragment.appendChild(createMentionChipElement(candidate, projectId))
    } else {
      fragment.appendChild(createMissingMentionChipElement(nodeId, projectId))
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    fragment.appendChild(document.createTextNode(content.slice(lastIndex)))
  }

  editor.innerHTML = ''
  editor.appendChild(fragment)
}

// ─── DropdownItem React Component ───────────────────────────────────────────

export interface DropdownItemProps {
  candidate: MentionCandidate
  isHighlighted: boolean
  projectId?: string
  onSelect: (candidate: MentionCandidate) => void
  onMouseEnter: () => void
}

export const DropdownItem = memo(function DropdownItem({
  candidate,
  isHighlighted,
  projectId,
  onSelect,
  onMouseEnter,
}: DropdownItemProps) {
  return React.createElement(
    'div',
    {
      'data-mention-item': true,
      role: 'option',
      'aria-selected': isHighlighted,
      className: `flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors ${
        isHighlighted ? 'bg-primary/15' : 'hover:bg-white/5'
      }`,
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault()
        onSelect(candidate)
      },
      onMouseEnter,
    },
    // Thumbnail or SVG icon fallback
    candidate.thumbnailPath && projectId
      ? React.createElement('img', {
          src: getThumbnailUrl(projectId, candidate.thumbnailPath, 0) ?? undefined,
          alt: '',
          className: 'w-6 h-6 rounded-sm object-cover flex-shrink-0 bg-black/20',
        })
      : React.createElement(
          'span',
          {
            className: 'w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground/50',
          },
          NODE_TYPE_ICON[candidate.type] || candidate.icon || '\u{1F4E6}',
        ),
    // SVG type icon
    React.createElement(
      'span',
      { className: 'w-3.5 h-3.5 flex-shrink-0 text-foreground/50' },
      NODE_TYPE_ICON[candidate.type] || React.createElement('span', { className: 'text-sm' }, candidate.icon),
    ),
    // Label
    React.createElement(
      'span',
      { className: 'flex-1 truncate text-foreground' },
      candidate.label,
    ),
    // Type
    React.createElement(
      'span',
      { className: 'flex-shrink-0 text-xs font-medium italic text-text-muted' },
      candidate.type,
    ),
  )
})
DropdownItem.displayName = 'DropdownItem'

// ─── Portal-based Mention Dropdown ──────────────────────────────────────────

export interface MentionDropdownPortalProps {
  candidates: MentionCandidate[]
  highlightIndex: number
  projectId?: string
  editorRef: React.RefObject<HTMLElement | null>
  dropdownRef?: React.RefObject<HTMLDivElement | null>
  onSelect: (candidate: MentionCandidate) => void
  onHighlight: (index: number) => void
}

/**
 * Portal-based mention dropdown rendered to `document.body`.
 * Positions itself above the editor element using getBoundingClientRect.
 */
export const MentionDropdownPortal = memo(function MentionDropdownPortal({
  candidates,
  highlightIndex,
  projectId,
  editorRef,
  dropdownRef: externalDropdownRef,
  onSelect,
  onHighlight,
}: MentionDropdownPortalProps) {
  const [editorRect, setEditorRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    const el = editorRef.current
    if (!el || candidates.length === 0) {
      setEditorRect(null)
      return
    }
    setEditorRect(el.getBoundingClientRect())
  }, [editorRef, candidates.length])

  if (candidates.length === 0 || !editorRect) return null

  const dropdown = React.createElement(
    'div',
    {
      ref: externalDropdownRef,
      id: 'chat-mention-dropdown',
      className:
        'fixed z-[9999] max-h-[200px] overflow-y-auto rounded-lg border border-border-subtle bg-surface-1 shadow-lg',
      style: {
        top: editorRect.top - 4,
        left: editorRect.left,
        width: editorRect.width,
        transform: 'translateY(-100%)',
      },
      role: 'listbox',
      'data-testid': 'mention-dropdown',
      // This dropdown is portaled to document.body (outside the chat <aside>),
      // so mark it too — interactions in the Director chat @mention list must
      // NOT count as canvas interaction (RFC: suppress auto-focus during canvas
      // interaction).
      'data-canvas-interaction-ignore': '',
    },
    candidates.map((candidate, index) =>
      React.createElement(DropdownItem, {
        key: candidate.id,
        candidate,
        isHighlighted: index === highlightIndex,
        projectId,
        onSelect,
        onMouseEnter: () => onHighlight(index),
      }),
    ),
  )

  return createPortal(dropdown, document.body)
})
MentionDropdownPortal.displayName = 'MentionDropdownPortal'
